import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { usePageTitle } from '../../hooks/usePageTitle'
import { supabase } from '../../lib/supabase'
import {
  HStack,
  Table,
  Badge,
  Avatar,
  Button,
  Banner,
  SkeletonTable,
  Select,
  Text
} from 'wss3-forge'
import { ArrowSync20Regular } from '@fluentui/react-icons'

interface AppUsage {
  app_id: string
  app_name: string
  last_login_at: string | null
  login_count: number
}

interface AdminUser {
  id?: string
  user_id: string
  email: string
  first_name: string | null
  last_name: string | null
  display_name: string | null
  avatar_url: string | null
  is_webba_member: boolean
  created_at: string
  last_sign_in_at: string | null
  email_confirmed_at: string | null
  is_admin: boolean
  is_disabled: boolean
  apps: AppUsage[]
}

interface AppOption {
  id: string
  name: string
}

export function Users() {
  const navigate = useNavigate()
  usePageTitle('Users')
  const [users, setUsers] = useState<AdminUser[]>([])
  const [applications, setApplications] = useState<AppOption[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isAdmin, setIsAdmin] = useState(false)
  const [filterAppId, setFilterAppId] = useState<string | null>(null)

  const loadApplications = async () => {
    const { data } = await supabase.rpc('admin_list_applications')
    setApplications(data || [])
  }

  const loadUsers = async (appId: string | null = filterAppId) => {
    setLoading(true)
    try {
      const { data: adminData } = await supabase.rpc('is_admin')
      setIsAdmin(adminData === true)

      if (!adminData) {
        setLoading(false)
        return
      }

      const { data, error: err } = await supabase.rpc('admin_list_users', {
        p_search: null,
        p_app_id: appId,
        p_limit: 100,
        p_offset: 0
      })

      if (err) throw err
      const usersWithId = (data || []).map((u: AdminUser) => ({ ...u, id: u.user_id }))
      setUsers(usersWithId)
    } catch (e: any) {
      console.error('Error:', e)
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadApplications()
    loadUsers()
  }, [])

  const handleFilterChange = (value: string | number | null) => {
    const appId = value === '' ? null : value as string
    setFilterAppId(appId)
    loadUsers(appId)
  }

  const formatDate = (date: string | null) => {
    if (!date) return '-'
    return new Date(date).toLocaleDateString('fr-FR')
  }

  const formatRelativeDate = (date: string | null) => {
    if (!date) return '-'
    const d = new Date(date)
    const now = new Date()
    const diff = now.getTime() - d.getTime()
    const hours = Math.floor(diff / (1000 * 60 * 60))
    const days = Math.floor(hours / 24)

    if (hours < 1) return 'Just now'
    if (hours < 24) return `${hours}h ago`
    if (days < 7) return `${days}d ago`
    return d.toLocaleDateString('fr-FR')
  }

  if (loading) {
    return (
      <div className="page-content">
        <SkeletonTable rows={5} columns={5} />
      </div>
    )
  }

  if (!isAdmin) {
    return (
      <div className="page-content">
        <Banner variant="error" title="Access Denied">
          You need admin permissions to view this page.
        </Banner>
      </div>
    )
  }

  if (error) {
    return (
      <div className="page-content">
        <Banner variant="error" title="Error">{error}</Banner>
      </div>
    )
  }

  return (
    <div className="page-content">
      <Table
        title="User Management"
        subtitle={`${users.length} users`}
        columns={[
          {
            key: 'avatar',
            header: '',
            render: (_, row) => (
              <Avatar
                src={row.avatar_url || undefined}
                name={row.email}
                size="sm"
              />
            )
          },
          {
            key: 'email',
            header: 'Email',
            sortable: true,
            render: (value) => <Text weight="medium">{value}</Text>
          },
          {
            key: 'first_name',
            header: 'First Name',
            sortable: true,
            render: (value) => value || <Text color="muted">-</Text>
          },
          {
            key: 'last_name',
            header: 'Last Name',
            sortable: true,
            render: (value) => value || <Text color="muted">-</Text>
          },
          {
            key: 'status',
            header: 'Status',
            render: (_, row) => (
              <HStack gap="xs">
                {row.is_disabled && <Badge variant="error">Disabled</Badge>}
                {row.is_admin && <Badge variant="primary">Admin</Badge>}
                {row.is_webba_member && <Badge variant="success">Member</Badge>}
                {!row.is_disabled && (row.email_confirmed_at ? (
                  <Badge variant="default">Verified</Badge>
                ) : (
                  <Badge variant="warning">Unverified</Badge>
                ))}
              </HStack>
            )
          },
          {
            key: 'apps',
            header: 'Applications',
            render: (_, row) => {
              const apps = row.apps || []
              if (apps.length === 0) {
                return <Text size="sm" color="muted">-</Text>
              }
              return (
                <HStack gap="xs" style={{ flexWrap: 'wrap' }}>
                  {apps.slice(0, 3).map((app) => (
                    <Badge key={app.app_id} size="sm" variant="default">{app.app_name}</Badge>
                  ))}
                  {apps.length > 3 && (
                    <Text size="xs" color="muted">+{apps.length - 3}</Text>
                  )}
                </HStack>
              )
            }
          },
          {
            key: 'last_sign_in_at',
            header: 'Last Login',
            sortable: true,
            render: (value) => formatRelativeDate(value)
          },
          {
            key: 'created_at',
            header: 'Joined',
            sortable: true,
            render: (value) => formatDate(value)
          }
        ]}
        data={users}
        searchable
        pagination
        pageSize={10}
        onRowClick={(row) => navigate(`/users/${row.user_id}`)}
        globalActions={
          <HStack gap="sm">
            <div style={{ minWidth: '200px' }}>
              <Select
                value={filterAppId || ''}
                onChange={handleFilterChange}
                options={[
                  { value: '', label: 'All applications' },
                  ...applications.map(app => ({ value: app.id, label: app.name }))
                ]}
              />
            </div>
            <Button variant="secondary" size="sm" icon={<ArrowSync20Regular />} onClick={() => loadUsers()}>
              Refresh
            </Button>
          </HStack>
        }
      />
    </div>
  )
}
